/**
 * export-widgets.ts — Widget Export Utility
 *
 * Provides PNG and PDF export of selected dashboard widgets.
 *
 * Public API:
 *   - generateWidgetPreviews  → returns outerHTML strings for the modal preview
 *   - downloadWidgetsAsPng    → captures widgets and downloads a single PNG
 *   - downloadWidgetsAsPdf    → captures widgets and downloads a multi-page A3 PDF
 *
 * Both download functions share the same capture pipeline (captureByRows) and
 * accept a PdfOptions object to stamp a branded header on the output:
 *   { title, subtitle, currency }
 *   e.g. { title: "RENDAUTO — INSIGHTS", subtitle: "Cut-off Date: 30/03/2026", currency: "Amounts presented in MXN" }
 *
 * PDF page layout (A3 landscape, pt units):
 *   Header  75pt  — background #CBD5E1, title / subtitle / currency
 *   Gap     16pt
 *   Content       — widgets scaled to fit width; scaled down if taller than the usable area (never sliced across pages)
 *   Footer  28pt  — separator line, "Confidential" left, "Page X of Y" right
 */

// No static imports of html-to-image / jspdf on purpose (LEN-2125): both were
// riding in the INITIAL bundle (~520 kB of source) although nothing needs them
// until the user actually exports — and the initial budget was 170 bytes from
// its hard ceiling. Loaded on demand below; webpack splits them into their own
// lazy chunks, fetched once on the first export.

/** Sorts widgets by DOM order so export matches the visual top-to-bottom sequence. */
function sortByDomOrder(widgets: HTMLElement[]): HTMLElement[] {
  return [...widgets].sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  );
}

/**
 * Deep-clones a widget and replaces every <canvas> with a static <img> snapshot.
 * This preserves Chart.js charts in the exported image since html-to-image cannot
 * read canvas pixels directly. Also strips the export-mode overlay (data-export-overlay).
 */
function cloneWithCanvases(widget: HTMLElement): HTMLElement {
  const clone = widget.cloneNode(true) as HTMLElement;
  clone.querySelector('[data-export-overlay]')?.remove();

  const originalCanvases = Array.from(widget.querySelectorAll('canvas'));
  const clonedCanvases = Array.from(clone.querySelectorAll('canvas'));
  originalCanvases.forEach((original, i) => {
    const cloned = clonedCanvases[i];
    if (!cloned) return;
    const img = document.createElement('img');
    img.src = original.toDataURL();
    img.className = cloned.className;
    const { width, height } = original.getBoundingClientRect();
    img.style.width = `${width}px`;
    img.style.height = `${height}px`;
    cloned.replaceWith(img);
  });

  return clone;
}

/** Groups widgets whose top edge is within 5px of each other into the same row, preserving multi-column grid layouts. */
function groupByVisualRow(widgets: HTMLElement[]): HTMLElement[][] {
  const rows: HTMLElement[][] = [];
  for (const widget of widgets) {
    const top = widget.getBoundingClientRect().top;
    const existing = rows.find(
      row => Math.abs(row[0].getBoundingClientRect().top - top) < 5
    );
    if (existing) {
      existing.push(widget);
    } else {
      rows.push([widget]);
    }
  }
  return rows;
}

/** Clones the parent grid element (preserving CSS classes and width) and appends the cloned widgets into it, keeping the original grid layout intact for capture. */
function buildRowContainer(
  rowWidgets: HTMLElement[],
  parentWidth: number
): HTMLElement {
  const parent = rowWidgets[0].parentElement!;
  const container = parent.cloneNode(false) as HTMLElement;
  container.style.width = `${parentWidth}px`;
  container.style.height = 'auto';
  rowWidgets.forEach(w => container.appendChild(cloneWithCanvases(w)));
  return container;
}

/**
 * Full capture pipeline: sorts widgets → groups into visual rows → builds each row
 * container off-screen → calls html-to-image toPng on each row → returns an array
 * of PNG data URLs, one per row.
 */
async function captureByRows(widgets: HTMLElement[]): Promise<string[]> {
  const { toPng } = await import('html-to-image');
  const sorted = sortByDomOrder(widgets);
  const rows = groupByVisualRow(sorted);
  const dataUrls: string[] = [];

  for (const rowWidgets of rows) {
    const parent = rowWidgets[0].parentElement!;
    const { width } = parent.getBoundingClientRect();
    const container = buildRowContainer(rowWidgets, width);

    const offscreen = document.createElement('div');
    offscreen.style.cssText =
      'position:absolute;left:-99999px;top:0;overflow:hidden;';
    offscreen.appendChild(container);
    document.body.appendChild(offscreen);

    try {
      dataUrls.push(await toPng(container, { backgroundColor: 'white' }));
    } finally {
      document.body.removeChild(offscreen);
    }
  }

  return dataUrls;
}

/**
 * Returns outerHTML strings (one per visual row) for populating the export modal preview.
 * Uses the same row-grouping logic as captureByRows but skips the async image capture.
 */
export function generateWidgetPreviews(widgets: HTMLElement[]): string[] {
  const sorted = sortByDomOrder(widgets);
  const rows = groupByVisualRow(sorted);

  return rows.map(rowWidgets => {
    const parent = rowWidgets[0].parentElement!;
    const { width } = parent.getBoundingClientRect();
    return buildRowContainer(rowWidgets, width).outerHTML;
  });
}

/** Stacks all row images onto a single canvas with uniform padding around the edges and a gap between rows. */
async function composeCanvas(
  dataUrls: string[],
  padding: number,
  gap: number
): Promise<HTMLCanvasElement> {
  const images = await Promise.all(
    dataUrls.map(
      url =>
        new Promise<HTMLImageElement>(resolve => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.src = url;
        })
    )
  );

  const totalWidth = Math.max(...images.map(img => img.width)) + padding * 2;
  const totalHeight =
    images.reduce((sum, img) => sum + img.height, 0) +
    gap * (images.length - 1) +
    padding * 2;

  const canvas = document.createElement('canvas');
  canvas.width = totalWidth;
  canvas.height = totalHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, totalWidth, totalHeight);

  let y = padding;
  images.forEach(img => {
    ctx.drawImage(img, padding, y);
    y += img.height + gap;
  });

  return canvas;
}

/**
 * Word-wraps `text` into as many lines as `maxWidth` requires, measured with
 * the font currently set on `ctx`. `fillText` has no wrapping of its own, and
 * the FX disclosure line (LEN-2092) runs ~240 characters — drawn unwrapped it
 * lost everything from the source onwards (MR !1207 B1).
 */
function wrapCanvasLine(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const lines: string[] = [];
  let current = '';

  for (const word of text.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (current && ctx.measureText(candidate).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) {
    lines.push(current);
  }

  return lines;
}

/**
 * Prepends a header band to the canvas for PNG export.
 * Draws the #CBD5E1 background and the text lines (title, subtitle, currency)
 * using Canvas 2D — no DOM capture, no html-to-image call.
 *
 * The band grows with its content: the currency line carries the FX
 * disclosure since LEN-2092 and wraps to as many lines as it needs, so the
 * height is computed from the wrapped line count rather than fixed at the
 * 90px that fit exactly three lines.
 */
async function addHeaderToCanvas(
  canvas: HTMLCanvasElement,
  title: string,
  subtitle: string,
  currency: string,
  operation?: string
): Promise<HTMLCanvasElement> {
  const MIN_HEADER_H = 90;
  const FOOTER_H = 40;
  const PAD = 20;
  const FONT_SIZE = 16;
  const LINE_GAP = 6;

  await document.fonts.ready;

  // Measure with the same font the lines are drawn with, before the output
  // canvas can exist — its height depends on how many lines come out.
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = `${FONT_SIZE}px 'Clear Sans', sans-serif`;
  const maxWidth = canvas.width - PAD * 2;
  const currencyLines = currency
    ? wrapCanvasLine(measure, currency, maxWidth)
    : [];

  const textLines = 1 + (subtitle ? 1 : 0) + currencyLines.length;
  const textBlockH = textLines * FONT_SIZE + (textLines - 1) * LINE_GAP;
  const HEADER_H = Math.max(MIN_HEADER_H, textBlockH + PAD * 2);

  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = canvas.height + HEADER_H + FOOTER_H;

  const ctx = out.getContext('2d')!;

  // Header
  ctx.fillStyle = '#CBD5E1';
  ctx.fillRect(0, 0, out.width, HEADER_H);

  let baselineY = (HEADER_H - textBlockH) / 2 + FONT_SIZE;
  const displayTitle = operation
    ? title.replace('—', `— ${operation} —`)
    : title;

  ctx.font = `bold ${FONT_SIZE}px 'Sofia Pro', sans-serif`;
  ctx.fillStyle = '#1e293b';
  ctx.fillText(displayTitle, PAD, baselineY);

  ctx.font = `${FONT_SIZE}px 'Clear Sans', sans-serif`;
  ctx.fillStyle = '#334155';

  if (subtitle) {
    baselineY += FONT_SIZE + LINE_GAP;
    ctx.fillText(subtitle, PAD, baselineY);
  }

  for (const line of currencyLines) {
    baselineY += FONT_SIZE + LINE_GAP;
    ctx.fillText(line, PAD, baselineY);
  }

  // Content
  ctx.drawImage(canvas, 0, HEADER_H);

  // Footer
  const footerY = HEADER_H + canvas.height;
  ctx.fillStyle = 'white';
  ctx.fillRect(0, footerY, out.width, FOOTER_H);

  ctx.strokeStyle = '#D2D2D2';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(PAD, footerY + 1);
  ctx.lineTo(out.width - PAD, footerY + 1);
  ctx.stroke();

  ctx.font = `12px 'helvetica', sans-serif`;
  ctx.fillStyle = '#64748B';
  ctx.fillText('Confidential', PAD, footerY + FOOTER_H - 10);
  ctx.textAlign = 'right';
  ctx.fillText('Page 1 of 1', out.width - PAD, footerY + FOOTER_H - 10);
  ctx.textAlign = 'left';

  return out;
}

/**
 * Captures the selected widgets and downloads them as a single PNG file.
 * If PdfOptions.title is provided, a branded header is stamped at the top.
 */
export async function downloadWidgetsAsPng(
  widgets: HTMLElement[],
  fileName: string,
  options?: PdfOptions
): Promise<void> {
  if (!widgets.length) return;

  const dataUrls = await captureByRows(widgets);
  let canvas = await composeCanvas(dataUrls, 32, 16);

  if (options?.title) {
    canvas = await addHeaderToCanvas(canvas, options.title, options.subtitle ?? '', options.currency ?? '', options.operation);
  }

  triggerDownload(
    canvas.toDataURL('image/png'),
    fileName.endsWith('.png') ? fileName : `${fileName}.png`
  );
}

export interface PdfOptions {
  title?: string;
  operation?: string;
  subtitle?: string;
  currency?: string;
}

/**
 * Renders the PDF header as a real DOM flex element so Sofia Pro / Clear Sans apply,
 * captures it with html-to-image at 2x pixel ratio, and returns a PNG data URL
 * plus the height the content actually needed, in pt.
 * The result is stamped at the top of every PDF page via drawPageDecorations.
 *
 * `headerHeightPt` is a minimum, not a fixed size: the currency line carries
 * the FX disclosure since LEN-2092 and wraps inside the fixed width, so a
 * fixed height clipped everything past the second line (MR !1207 B1). The
 * caller lays the page out with the returned height.
 */
async function captureHeaderImage(
  title: string,
  subtitle: string,
  currency: string,
  pageWidthPt: number,
  headerHeightPt: number,
  operation?: string
): Promise<{ dataUrl: string; heightPt: number }> {
  // 96dpi CSS pixel equivalents of the pt dimensions (1pt = 96/72 px)
  const PX_PER_PT = 96 / 72;
  const w = Math.round(pageWidthPt * PX_PER_PT);
  const h = Math.round(headerHeightPt * PX_PER_PT);
  // 12pt → 16px at 96 dpi
  const fontSize = Math.round(12 * PX_PER_PT);
  const pad = Math.round(20 * PX_PER_PT);
  const vPad = Math.round(8 * PX_PER_PT);

  const container = document.createElement('div');
  container.style.cssText =
    `width:${w}px;min-height:${h}px;background:#CBD5E1;` +
    `display:flex;flex-direction:column;justify-content:center;` +
    `padding:${vPad}px ${pad}px;box-sizing:border-box;`;

  const titleEl = document.createElement('p');
  titleEl.style.cssText =
    `font-family:'Sofia Pro',sans-serif;font-weight:700;font-size:${fontSize}px;` +
    `color:#1e293b;margin:0;line-height:1.3;`;
  titleEl.textContent = operation ? title.replace('—', `— ${operation} —`) : title;

  const lineMargin = `${Math.round(3 * PX_PER_PT)}px 0 0`;
  const subtitleEl = document.createElement('p');
  subtitleEl.style.cssText =
    `font-family:'Clear Sans',sans-serif;font-weight:400;font-size:${fontSize}px;` +
    `color:#334155;margin:${lineMargin};line-height:1.3;`;
  subtitleEl.textContent = subtitle;

  const currencyEl = document.createElement('p');
  currencyEl.style.cssText =
    `font-family:'Clear Sans',sans-serif;font-weight:400;font-size:${fontSize}px;` +
    `color:#334155;margin:${lineMargin};line-height:1.3;`;
  currencyEl.textContent = currency;

  container.appendChild(titleEl);
  if (subtitle) container.appendChild(subtitleEl);
  if (currency) container.appendChild(currencyEl);

  const offscreen = document.createElement('div');
  offscreen.style.cssText = 'position:absolute;left:-99999px;top:0;overflow:hidden;';
  offscreen.appendChild(container);
  document.body.appendChild(offscreen);

  await document.fonts.ready;

  try {
    // Measured after mounting: this is the height the wrapped lines actually
    // occupy, which the caller must reserve on every page. Never below the
    // requested minimum — `min-height` guarantees that in a browser, but an
    // environment without layout (jsdom) reports 0 and would collapse the
    // band.
    const heightPt = Math.max(
      headerHeightPt,
      container.offsetHeight / PX_PER_PT
    );
    const { toPng } = await import('html-to-image');
    const dataUrl = await toPng(container, {
      pixelRatio: 2,
      backgroundColor: '#CBD5E1',
    });
    return { dataUrl, heightPt };
  } finally {
    document.body.removeChild(offscreen);
  }
}

/**
 * Captures the selected widgets and downloads them as a multi-page A3 landscape PDF.
 *
 * Each widget row is scaled to fit the usable width. If it would exceed the usable
 * height, it is scaled down further so it always fits on one page (never sliced).
 * A new page is added whenever the remaining vertical space is insufficient.
 *
 * Every page gets a header (captureHeaderImage) and footer (drawPageDecorations):
 *   header  — 75pt band: title / subtitle / currency
 *   footer  — separator line, "Confidential" left, "Page X of Y" right
 */
export async function downloadWidgetsAsPdf(
  widgets: HTMLElement[],
  fileName: string,
  options?: PdfOptions
): Promise<void> {
  if (!widgets.length) return;

  const dataUrls = await captureByRows(widgets);
  const name = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
  const pagePadding = 20;
  const rowGap = 10;
  const minHeaderHeight = 75;
  const footerHeight = 28;
  const totalPagesAlias = '{total_pages_count_string}';

  const images = await Promise.all(
    dataUrls.map(
      url =>
        new Promise<HTMLImageElement>(resolve => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.src = url;
        })
    )
  );

  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - pagePadding * 2;

  // Captured before the layout math: the header owns its height now (the FX
  // disclosure line wraps, LEN-2092), so the content area starts wherever the
  // rendered header actually ends instead of at a fixed 75pt.
  const header = options?.title
    ? await captureHeaderImage(
      options.title,
      options.subtitle ?? '',
      options.currency ?? '',
      pageWidth,
      minHeaderHeight,
      options.operation
    )
    : undefined;

  const headerHeight = header?.heightPt ?? minHeaderHeight;
  const contentStartY = headerHeight + 16;
  const contentEndY = pageHeight - footerHeight;
  const usableHeight = contentEndY - contentStartY;

  const drawPageDecorations = () => {
    const pageNum = doc.getNumberOfPages();

    if (header) {
      doc.addImage(header.dataUrl, 'PNG', 0, 0, pageWidth, headerHeight);
    }

    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.5);
    doc.line(pagePadding, contentEndY, pageWidth - pagePadding, contentEndY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(100, 116, 139);
    doc.text('Confidential', pagePadding, pageHeight - 10);
    doc.text(
      `Page ${pageNum} of ${totalPagesAlias}`,
      pageWidth - pagePadding,
      pageHeight - 10,
      { align: 'right' }
    );
  };

  drawPageDecorations();

  let y = contentStartY;
  let isFirst = true;

  for (const img of images) {
    // Scale to fit width, then clamp to usable height if needed (scale down, never slice)
    const widthScale = usableWidth / img.width;
    const heightScale = (img.height * widthScale) > usableHeight
      ? usableHeight / (img.height * widthScale) * widthScale
      : widthScale;
    const imgWidthPt = img.width * heightScale;
    const imgHeightPt = img.height * heightScale;

    if (!isFirst) {
      const remaining = contentEndY - y;
      if (remaining < imgHeightPt) {
        doc.addPage();
        drawPageDecorations();
        y = contentStartY;
      } else {
        y += rowGap;
      }
    }
    isFirst = false;

    // Center horizontally if scaled down below full width
    const xOffset = pagePadding + (usableWidth - imgWidthPt) / 2;
    doc.addImage(img, 'PNG', xOffset, y, imgWidthPt, imgHeightPt);
    y += imgHeightPt;
  }

  doc.putTotalPages(totalPagesAlias);
  doc.save(name);
}

/** Creates a temporary <a> element and clicks it to trigger the browser file download. */
function triggerDownload(dataUrl: string, fileName: string): void {
  const a = document.createElement('a');
  a.download = fileName;
  a.href = dataUrl;
  a.click();
}
